import { useEffect, useState } from "react";
import {
  Bot,
  Check,
  CheckCircle2,
  Copy,
  FileText,
  Link2,
  MessageSquareText,
  MonitorDown,
  Settings2,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import SlideGuide, {
  type GuideSlide,
  type GuideStep,
} from "@/components/training/SlideGuide";
import {
  ADAPTIVE_REASONING_PROTOCOL,
  CUSTOM_INSTRUCTIONS_EXAMPLE,
  HOW_MIGHT_WE_PROMPT,
} from "@/data/claudeBasics";
import { trackEvent } from "@/utils/posthog";

const CHIEF_OF_STAFF_SHORT_PROMPT =
  "Turn this thread into a heartbeat, I want you to check my emails, slack, clickup every 3 hours and tell me what to prioritize. Make sure everything has a link";

const STEPS: GuideStep[] = [
  {
    id: "desktop-app",
    title: "Pick one desktop agent",
    description: "Choose ChatGPT or Claude as the home base for your AI work.",
    duration: "5 min",
    whyItMatters:
      "You will move faster when your chats, files, connections, and recurring work live in one place. Pick one to start. You can always add the other later.",
    actions: [
      "Choose ChatGPT Desktop or Claude Desktop. You only need one for this guide.",
      "Download the official app and finish the setup.",
      "Sign in, start a conversation, then find it again in your history.",
      "Pin the app somewhere you will actually use it during the day.",
    ],
    checkpoint:
      "You are signed in and can start, close, and reopen a conversation.",
    icon: MonitorDown,
    images: [
      {
        src: "/screenshots/ai-agent-starter/chatgpt-work-home.jpg",
        alt: "ChatGPT Desktop home screen.",
        caption: "ChatGPT keeps chats, files, and connected tools in one place.",
        label: "ChatGPT",
      },
      {
        src: "/screenshots/masterclass/27-cowork-intro.png",
        alt: "Claude Desktop home screen.",
        caption: "Claude gives you the same kind of home base for ongoing work.",
        label: "Claude",
      },
    ],
    links: [
      { label: "Download ChatGPT Desktop", href: "https://openai.com/chatgpt/desktop/" },
      { label: "Download Claude Desktop", href: "https://claude.ai/download" },
    ],
    note: "Pick the tool your team supports and your data policy allows. The workflow matters more than the logo.",
  },
  {
    id: "privacy",
    title: "Set the rules before you share real work",
    description: "Decide what the agent can remember, retain, and learn from.",
    duration: "7 min",
    whyItMatters:
      "More context makes an agent more useful. It also raises the stakes. Set the limits before you connect anything.",
    actions: [
      "Open Settings and find Data Controls or Privacy.",
      "Decide whether your conversations can be used to improve the model.",
      "Review memory and past-chat search. Turn them on only if your policy allows it.",
      "Write one sentence naming the information you will never paste or connect.",
    ],
    checkpoint:
      "You can explain your choices for training, history, memory, and sensitive information to a teammate.",
    icon: ShieldCheck,
    images: [
      {
        src: "/screenshots/ai-agent-starter/chatgpt-data-controls.jpg",
        alt: "ChatGPT data controls settings.",
        caption: "Review training, history, memory, and connected data before using company work.",
        label: "ChatGPT",
      },
      {
        src: "/screenshots/masterclass/09-privacy.png",
        alt: "Claude privacy settings.",
        caption: "The labels may change. The decision does not: know what is retained and how it is used.",
        label: "Claude",
      },
    ],
    note: "Never paste passwords, account numbers, private health records, or confidential information you are not authorized to share.",
  },
  {
    id: "instructions",
    title: "Tell the agent how you work",
    description: "Add your role, audience, voice, and decision rules once.",
    duration: "10 min",
    whyItMatters:
      "Good standing instructions save you from repeating yourself. They give the agent enough context to be useful without forcing every task into the same answer style.",
    actions: [
      "Open Personalization or Profile settings and find Custom Instructions.",
      "Add your role, audience, preferred format, voice, and important limits.",
      "Add the Adaptive Reasoning Protocol so the agent changes its approach with the task.",
      "Try one real request. Fix anything that feels generic, stiff, or overly complicated.",
    ],
    checkpoint:
      "A new conversation understands your role and preferences, then changes its approach based on the request.",
    icon: Settings2,
    prompts: [
      { label: "Custom Instructions example", value: CUSTOM_INSTRUCTIONS_EXAMPLE },
      { label: "Adaptive Reasoning Protocol", value: ADAPTIVE_REASONING_PROTOCOL },
    ],
    images: [
      {
        src: "/screenshots/ai-agent-starter/chatgpt-personalization.jpg",
        alt: "ChatGPT personalization and custom instructions settings.",
        caption: "ChatGPT keeps standing preferences in Personalization.",
        label: "ChatGPT",
      },
      {
        src: "/screenshots/masterclass/07-adaptive-reasoning.png",
        alt: "Adaptive Reasoning Protocol inside Claude instructions.",
        caption: "Claude can use the same protocol in profile preferences or project instructions.",
        label: "Claude",
      },
    ],
  },
  {
    id: "connectors",
    title: "Connect one source of truth",
    description: "Let the agent read one approved tool that holds real work.",
    duration: "8 min",
    whyItMatters:
      "Without context, AI waits for you to paste everything. One approved connection lets it find current information and link you straight to the evidence.",
    actions: [
      "Open Apps, Connectors, or Connections in your desktop app.",
      "Choose one low-risk source you use often, such as Google Drive, Slack, or your project tool.",
      "Review the permissions, connect the account, and confirm the right workspace.",
      "Ask one read-only question and require a link to every source used.",
    ],
    checkpoint:
      "The agent answers one read-only question from an approved source and gives you a working link to the evidence.",
    icon: Link2,
    images: [
      {
        src: "/screenshots/ai-agent-starter/chatgpt-plugins.jpg",
        alt: "ChatGPT apps and connectors screen.",
        caption: "Start with one connector that removes a real bottleneck.",
        label: "ChatGPT",
      },
      {
        src: "/screenshots/masterclass/21-connections-mcp.png",
        alt: "Claude connections settings.",
        caption: "Claude connections provide the same bridge to approved work.",
        label: "Claude",
      },
    ],
    note: "Start read-only. Add permissions only when the workflow and review step are clear.",
  },
  {
    id: "workflow",
    title: "Turn one weekly task into a workflow",
    description: "Save the instructions so the agent can do the work the same way next time.",
    duration: "12 min",
    whyItMatters:
      "The real gain starts when a good conversation becomes a repeatable way of working. Save the context, expected output, and quality bar so you are not starting over next week.",
    actions: [
      "Choose one weekly task. Name the trigger, inputs, expected output, and quality bar.",
      "Create a Project, Skill, or saved workspace for that task.",
      "Add one strong example and enable only the capabilities the task needs.",
      "Run it on current information, inspect the result, then save what you improved.",
    ],
    checkpoint:
      "You can open the saved workflow, add current inputs, and get a useful result without rebuilding the instructions.",
    icon: Bot,
    images: [
      {
        src: "/screenshots/ai-agent-starter/chatgpt-create-project.jpg",
        alt: "Creating a project in ChatGPT.",
        caption: "A Project keeps instructions, examples, files, and related chats together.",
        label: "ChatGPT",
      },
      {
        src: "/screenshots/masterclass/23-install-skills.png",
        alt: "Installing a reusable skill in Claude.",
        caption: "A Claude Skill packages repeatable instructions and the resources it needs.",
        label: "Claude",
      },
    ],
    note: "It is ready when another person can run it and recognize a good result.",
  },
];

const SLIDES: GuideSlide[] = [
  { id: "start", title: "AI Agent Starter", kind: "cover", tone: "dark" },
  { id: "outcomes", title: "What you will set up", kind: "outcomes", tone: "paper" },
  { id: "roadmap", title: "Five steps. One useful agent.", kind: "roadmap", tone: "blue" },
  { id: "step-desktop-app", title: "Choose one home base", kind: "activity", tone: "paper", stepId: "desktop-app", showWhy: true, actionIndexes: [0, 1], showImages: true, showLinks: true },
  { id: "desktop-app-ready", title: "Put the agent where you will use it", kind: "activity", tone: "blue", stepId: "desktop-app", actionIndexes: [2, 3], showNote: true },
  { id: "desktop-app-check", title: "Your home base is ready", kind: "checkpoint", tone: "orange", stepId: "desktop-app" },
  { id: "step-privacy", title: "Set the data boundary first", kind: "activity", tone: "paper", stepId: "privacy", showWhy: true, actionIndexes: [0, 1], showImages: true },
  { id: "privacy-controls", title: "A practical starting point", kind: "custom", tone: "paper", stepId: "privacy" },
  { id: "privacy-rule", title: "Write one rule your team can follow", kind: "activity", tone: "blue", stepId: "privacy", actionIndexes: [2, 3], showNote: true },
  { id: "privacy-check", title: "Your privacy rules are clear", kind: "checkpoint", tone: "orange", stepId: "privacy" },
  { id: "step-instructions", title: "Give the agent the context you repeat", kind: "activity", tone: "paper", stepId: "instructions", showWhy: true, actionIndexes: [0, 1], showImages: true },
  { id: "instructions-prompts", title: "Add your context and a flexible method", kind: "prompt", tone: "dark", stepId: "instructions", promptIndexes: [0, 1], actionIndexes: [2, 3] },
  { id: "instructions-check", title: "A new chat understands how you work", kind: "checkpoint", tone: "orange", stepId: "instructions" },
  { id: "step-connectors", title: "Connect one source of truth", kind: "activity", tone: "paper", stepId: "connectors", showWhy: true, actionIndexes: [0, 1], showImages: true },
  { id: "connectors-test", title: "Test it without changing anything", kind: "activity", tone: "blue", stepId: "connectors", actionIndexes: [2, 3], showNote: true },
  { id: "connectors-check", title: "The agent can find and cite real work", kind: "checkpoint", tone: "orange", stepId: "connectors" },
  { id: "step-workflow", title: "Start with one task you already repeat", kind: "activity", tone: "paper", stepId: "workflow", showWhy: true, actionIndexes: [0, 1], showImages: true },
  { id: "workflow-test", title: "Run it. Review it. Improve it.", kind: "activity", tone: "blue", stepId: "workflow", actionIndexes: [2, 3], showNote: true },
  { id: "workflow-check", title: "Your first workflow is ready to reuse", kind: "checkpoint", tone: "orange", stepId: "workflow" },
  { id: "how-might-we", title: "Use How Might We to design the next workflow", kind: "custom", tone: "paper" },
  { id: "chief-of-staff", title: "See what this can become", kind: "custom", tone: "blue" },
  { id: "chief-of-staff-prompt", title: "Build your first Chief of Staff heartbeat", kind: "custom", tone: "dark" },
  { id: "finish", title: "You have a working AI home base", kind: "finish", tone: "dark" },
];

const copyText = async (value: string) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  textarea.remove();
};

const CopyBlock = ({ label, value, printMode = false }: { label: string; value: string; printMode?: boolean }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await copyText(value);
      setCopied(true);
      trackEvent("AI Agent Starter Prompt Copied", { prompt: label });
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className="overflow-hidden rounded-2xl border border-white/15 bg-[#020817] text-white shadow-2xl shadow-black/20">
      <div className="flex items-center justify-between gap-4 border-b border-white/10 px-4 py-3 sm:px-5">
        <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-orange-300">{label}</p>
        {!printMode && <button type="button" onClick={() => void handleCopy()} className="inline-flex min-h-10 items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-4 text-sm font-bold hover:border-orange-400 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400">{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}{copied ? "Copied" : "Copy"}</button>}
      </div>
      <pre className="max-h-[47svh] overflow-auto whitespace-pre-wrap px-4 py-5 font-mono text-[13px] leading-6 text-slate-100 sm:px-5 sm:text-sm">{value}</pre>
    </div>
  );
};

const PrivacyControls = () => (
  <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center py-8 sm:py-12">
    <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-orange-700">Step 2 · practical defaults</p>
    <h1 className="mt-4 max-w-5xl font-serif text-4xl font-semibold leading-tight tracking-[-0.04em] sm:text-5xl lg:text-6xl">Choose the convenience you can support responsibly.</h1>
    <div className="mt-7 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="grid grid-cols-[0.8fr_0.8fr_1.4fr] gap-3 bg-slate-950 px-4 py-3 text-xs font-extrabold uppercase tracking-[0.12em] text-white sm:px-6"><span>Control</span><span>Starting point</span><span>Decision rule</span></div>
      {[
        ["Chat history", "On", "Keep it when searchable work history is useful and allowed."],
        ["Model training", "Off for private work", "Turn it off when using client, confidential, or internal company information."],
        ["Memory", "Policy dependent", "Use it only when saved personal context fits your policy."],
        ["Past-chat search", "On if useful", "Enable it when prior chats are an approved source for current work."],
      ].map(([control, startingPoint, decision]) => <div key={control} className="grid grid-cols-[0.8fr_0.8fr_1.4fr] gap-3 border-t border-slate-200 px-4 py-4 text-sm leading-relaxed text-slate-700 sm:px-6"><strong className="text-slate-950">{control}</strong><span className="font-semibold text-orange-700">{startingPoint}</span><span>{decision}</span></div>)}
    </div>
  </div>
);

const HowMightWeSlide = ({ printMode }: { printMode: boolean }) => (
  <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center py-8 sm:py-12">
    <div className="flex items-center gap-3"><span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-orange-500 text-white"><Sparkles className="h-5 w-5" /></span><p className="text-xs font-extrabold uppercase tracking-[0.18em] text-orange-700">Bonus · design the next workflow</p></div>
    <h1 className="mt-5 max-w-5xl font-serif text-4xl font-semibold leading-tight tracking-[-0.04em] sm:text-5xl lg:text-6xl">Start with “How might we?” when you know the problem, but not the workflow.</h1>
    <p className="mt-4 max-w-3xl text-lg leading-relaxed text-slate-600">It gives the agent room to clarify the job before jumping to an answer. Replace the example with one bottleneck from your week.</p>
    <div className="mt-7"><CopyBlock label="How Might We prompt" value={HOW_MIGHT_WE_PROMPT} printMode={printMode} /></div>
  </div>
);

const ChiefOfStaffSlide = () => (
  <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center py-8 sm:py-12">
    <div className="flex items-center gap-3"><span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-orange-500 text-white"><MessageSquareText className="h-5 w-5" /></span><p className="text-xs font-extrabold uppercase tracking-[0.18em] text-orange-300">The payoff · one clear heartbeat</p></div>
    <h1 className="mt-5 max-w-5xl font-serif text-4xl font-semibold leading-tight tracking-[-0.04em] sm:text-5xl lg:text-6xl">Ask what needs your attention, not what happened everywhere.</h1>
    <div className="mt-7 grid gap-4 lg:grid-cols-3">
      {[
        ["Do", "Approve the pricing exception", "A decision is waiting on you."],
        ["Delegate to AI", "Draft the renewal recap", "The agent has enough context to draft it."],
        ["Discuss", "Confirm the launch date", "The tradeoff needs a human conversation."],
      ].map(([lane, task, reason]) => <div key={lane} className="rounded-2xl border border-white/15 bg-white/[0.06] p-5"><p className="text-xs font-extrabold uppercase tracking-[0.16em] text-orange-300">{lane}</p><h2 className="mt-4 text-xl font-bold text-white">{task}</h2><p className="mt-2 text-sm leading-relaxed text-slate-400">{reason}</p><span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-sky-300"><Link2 className="h-4 w-4" />Direct source link</span></div>)}
    </div>
    <div className="mt-6 flex items-start gap-3 rounded-2xl border border-emerald-300/20 bg-emerald-300/10 p-4 text-sm leading-relaxed text-emerald-50"><ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-300" /><p>Start read-only. Let the agent gather, sort, and link. Keep approvals, messages, task changes, and other outside actions behind human review.</p></div>
  </div>
);

const ChiefOfStaffPromptSlide = ({ fullPrompt, printMode }: { fullPrompt: string; printMode: boolean }) => (
  <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center py-7 sm:py-10">
    <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-orange-300">Copy, customize, run</p>
    <h1 className="mt-4 max-w-5xl font-serif text-4xl font-semibold leading-tight tracking-[-0.04em] sm:text-5xl lg:text-6xl">Turn this setup into a Chief of Staff heartbeat.</h1>
    <div className="mt-7 grid gap-5 lg:grid-cols-2">
      <CopyBlock label="Quick-start prompt" value={CHIEF_OF_STAFF_SHORT_PROMPT} printMode={printMode} />
      <CopyBlock label="Full Chief of Staff prompt" value={fullPrompt || "Loading the full prompt..."} printMode={printMode} />
    </div>
  </div>
);

const AIAgentStarter = () => {
  const [fullPrompt, setFullPrompt] = useState("");

  useEffect(() => {
    let active = true;
    fetch("/chief-of-staff-agent-prompt.txt")
      .then((response) => response.ok ? response.text() : Promise.reject(new Error("Prompt unavailable")))
      .then((value) => { if (active) setFullPrompt(value.trim()); })
      .catch(() => { if (active) setFullPrompt(CHIEF_OF_STAFF_SHORT_PROMPT); });
    return () => { active = false; };
  }, []);

  return (
    <SlideGuide
      name="AI Agent Starter"
      trackingName="AI Agent Starter Slide Guide"
      canonicalPath="/ai-agent-starter"
      storageKey="ai-agent-starter-completed-steps"
      slideStorageKey="ai-agent-starter-last-slide"
      seo={{
        title: "AI Agent Starter: Step-by-Step Setup Guide",
        description: "Set up ChatGPT Desktop or Claude Desktop in five practical steps, then turn one weekly task into a reusable AI workflow.",
        keywords: ["AI agent starter", "ChatGPT Desktop", "Claude Desktop", "AI workflow", "Chief of Staff agent"],
        image: "https://www.technical-leaders.com/ai-agent-starter-og.png",
      }}
      schema={{
        description: "A five-step guide to choosing, securing, personalizing, connecting, and using a desktop AI agent.",
        totalTime: "PT42M",
        supplies: ["ChatGPT Desktop or Claude Desktop", "One approved work connector"],
      }}
      cover={{
        title: "AI Agent",
        accent: "Starter",
        description: "Set up ChatGPT Desktop or Claude Desktop for real work, then turn one weekly task into a reusable workflow.",
        badges: ["5 steps", "About 45 minutes", "ChatGPT or Claude", "Progress saved"],
        finishLineTitle: "Your first agent workspace",
        finishLineBody: "You will leave with clear privacy settings, standing instructions, one approved connection, a reusable workflow, and a Chief of Staff prompt.",
      }}
      outcomes={{
        eyebrow: "From chat to agent",
        heading: "Start simple. Build something you will actually use.",
        items: ["One desktop AI home base", "Clear rules for private work", "Standing context and flexible reasoning", "One approved connection to real work", "One reusable weekly workflow", "A path to a cross-tool Chief of Staff heartbeat"],
        summary: "Each step makes the agent more useful while keeping you in control.",
      }}
      roadmapHeading="Choose the home base. Set the boundary. Add context. Connect work. Save the workflow."
      steps={STEPS}
      slides={SLIDES}
      contentsExtras={[
        { slideId: "how-might-we", label: "How Might We prompt" },
        { slideId: "chief-of-staff", label: "Chief of Staff example" },
        { slideId: "chief-of-staff-prompt", label: "Chief of Staff prompt" },
      ]}
      renderCustomSlide={(slide, context) => {
        if (slide.id === "privacy-controls") return <PrivacyControls />;
        if (slide.id === "how-might-we") return <HowMightWeSlide printMode={context.printMode} />;
        if (slide.id === "chief-of-staff") return <ChiefOfStaffSlide />;
        if (slide.id === "chief-of-staff-prompt") return <ChiefOfStaffPromptSlide fullPrompt={fullPrompt} printMode={context.printMode} />;
        return null;
      }}
      finish={{
        eyebrow: "Your workspace is ready",
        heading: "Now make one workflow genuinely useful.",
        body: "Use the same pattern for your next bottleneck: name the job, set the rules, connect only what it needs, save the method, and review the result.",
        cards: [
          { eyebrow: "Go deeper", title: "Claude Fundamentals", body: "Build confidence with Claude's core controls, projects, files, and reusable workflows.", href: "/claude-fundamentals" },
          { eyebrow: "Build with evidence", title: "The AI Builder Playbook", body: "Turn one Job Brief into a bounded, tested prototype with a clear human handoff.", href: "/ai-builder-playbook" },
        ],
      }}
    />
  );
};

export default AIAgentStarter;
